import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Inference } from "../../../types/inferences";
import { getInferenceId, getCompositeIds } from "../inferenceHelpers";

type ValidateFn = (options: {
  validationId: number;
  isValid: boolean;
  compositeIds?: number[];
}) => Promise<void> | undefined;

type UpdateInferenceFn = (options: {
  inferenceId: number;
  notes?: string;
  selected_class?: string | null;
  flagged?: boolean;
  compositeIds?: number[];
}) => Promise<void> | undefined;

type UseKeyboardShortcutsOptions = {
  sceneId: string;
  selectedInference: Inference | undefined;
  compositeMode: boolean;
  setCompositeMode: (value: boolean) => void;
  currentIndex: number;
  inferencesSortedLength: number;
  nextInferenceId: number | false;
  previousInferenceId: number | false;
  isValidating: boolean;
  isUpdating: boolean;
  validate: ValidateFn;
  updateInference: UpdateInferenceFn;
  // Disable all shortcuts, e.g. while the shortcuts modal itself is open
  disabled: boolean;
};

// Input types that accept free-form character entry. Radio/checkbox/button/range etc.
// don't, so a shortcut key pressed while one of those has focus should still fire.
const TEXT_LIKE_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "url",
  "tel",
  "password",
  "number",
  "date",
  "datetime-local",
  "month",
  "week",
  "time",
]);

// Don't fire shortcuts while the user is typing in a field (notes, composite count, etc).
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.tagName === "TEXTAREA" || target.tagName === "SELECT") return true;
  if (target.tagName === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return TEXT_LIKE_INPUT_TYPES.has(type);
  }
  return false;
}

// Radio/range inputs use left/right arrow keys natively to change their value
// (e.g. moving between the Manual Label radio options), so the arrow-key
// navigation shortcuts shouldn't hijack that while one of those has focus.
function usesArrowKeysNatively(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement) || target.tagName !== "INPUT") return false;
  const type = (target as HTMLInputElement).type;
  return type === "radio" || type === "range";
}

function useKeyboardShortcuts({
  sceneId,
  selectedInference,
  compositeMode,
  setCompositeMode,
  currentIndex,
  inferencesSortedLength,
  nextInferenceId,
  previousInferenceId,
  isValidating,
  isUpdating,
  validate,
  updateInference,
  disabled,
}: UseKeyboardShortcutsOptions) {
  const navigate = useNavigate();
  // Announced via an aria-live region so keyboard-triggered state changes (which have
  // no click target to carry visual focus) are also communicated to screen readers.
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (disabled) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (
        (e.key === "ArrowLeft" || e.key === "ArrowRight") &&
        usesArrowKeysNatively(e.target)
      ) {
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        navigate(`/scenes/${sceneId}`);
        return;
      }

      if (!selectedInference) return;

      switch (e.key.toLowerCase()) {
        case "p":
        case "arrowleft":
          if (currentIndex > 0 && !isValidating && previousInferenceId !== false) {
            navigate(`/scenes/${sceneId}/validate/${previousInferenceId}`);
          }
          break;
        case "n":
        case "arrowright":
          if (
            currentIndex < inferencesSortedLength - 1 &&
            !isValidating &&
            nextInferenceId !== false
          ) {
            navigate(`/scenes/${sceneId}/validate/${nextInferenceId}`);
          }
          break;
        case "v":
          if (!isValidating) {
            validate({
              validationId: getInferenceId(selectedInference, compositeMode),
              isValid: true,
              compositeIds: getCompositeIds(selectedInference, compositeMode),
            });
            updateInference({
              inferenceId: getInferenceId(selectedInference, compositeMode),
              selected_class: null,
              compositeIds: getCompositeIds(selectedInference, compositeMode),
            });
            setAnnouncement("Marked as valid");
          }
          break;
        case "i":
          if (!isValidating) {
            validate({
              validationId: getInferenceId(selectedInference, compositeMode),
              isValid: false,
              compositeIds: getCompositeIds(selectedInference, compositeMode),
            });
            setAnnouncement("Marked as invalid");
          }
          break;
        case "f":
          if (!isUpdating) {
            const nextFlagged = !selectedInference.properties.flagged;
            updateInference({
              inferenceId: getInferenceId(selectedInference, compositeMode),
              flagged: nextFlagged,
              compositeIds: getCompositeIds(selectedInference, compositeMode),
            });
            setAnnouncement(nextFlagged ? "Detection flagged" : "Detection unflagged");
          }
          break;
        case "c":
          setCompositeMode(!compositeMode);
          setAnnouncement(compositeMode ? "Composite mode off" : "Composite mode on");
          break;
        default:
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    disabled,
    sceneId,
    selectedInference,
    compositeMode,
    setCompositeMode,
    currentIndex,
    inferencesSortedLength,
    nextInferenceId,
    previousInferenceId,
    isValidating,
    isUpdating,
    validate,
    updateInference,
    navigate,
  ]);

  return { announcement };
}

export default useKeyboardShortcuts;
